// Canvas-рушій заставки:
// - "затяжки" пристроїв за розкладом (вмикає CSS-класи is-firing-* на корені,
//   від яких світяться випаровувач, екран, корпус одноразки);
// - боке-частинки на фоні;
// - прогрес завантаження, який асимптотично росте до 96% і добігає до 100% на finish().

const TAU = Math.PI * 2;
const CYCLE_MS = 3600;
const BOKEH_COLORS = ["#7ea6ff", "#a78bfa", "#f472b6", "#5eead4", "#fde68a"];

const rand = (a, b) => a + Math.random() * (b - a);

function makeBokehSprite(color, size = 64) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  const r = size / 2;
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  grad.addColorStop(0, color);
  grad.addColorStop(0.35, `${color}aa`);
  grad.addColorStop(1, `${color}00`);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return c;
}

/**
 * @param {object} opts
 * @param {HTMLElement} opts.root            елемент, на який вішаються класи is-firing-*
 * @param {HTMLCanvasElement} opts.canvas
 * @param {Array} opts.emitters              [{ id, offset, duration }] — розклад «затяжок»
 * @param {(percent: number) => void} opts.onProgress
 * @param {boolean} opts.reducedMotion
 * @param {number} opts.expectedMs           орієнтовна тривалість показу — під неї розтягується прогрес
 * @param {() => void} [opts.onDone]         викликається один раз, коли показано expectedMs анімації
 */
export function startSplashEngine({
  root,
  canvas,
  emitters,
  onProgress,
  reducedMotion,
  expectedMs = 3500,
  onDone,
}) {
  const ctx = canvas.getContext("2d");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let width = 0;
  let height = 0;

  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  };
  resize();
  window.addEventListener("resize", resize);

  const bokehSprites = BOKEH_COLORS.map((c) => makeBokehSprite(c));

  const spawnBokeh = (anywhere) => {
    const big = Math.random() < 0.25;
    return {
      x: rand(0, width),
      y: anywhere ? rand(0, height) : height + 20,
      r: big ? rand(7, 15) : rand(1.2, 3.6),
      alpha: big ? rand(0.06, 0.14) : rand(0.25, 0.6),
      vy: -rand(6, 18),
      phase: rand(0, TAU),
      sprite: bokehSprites[(Math.random() * bokehSprites.length) | 0],
    };
  };

  const bokeh = Array.from({ length: 26 }, () => spawnBokeh(true));
  const state = emitters.map((e) => ({ ...e, firing: false }));

  let last = performance.now();
  // Час анімації рахуємо з обмежених dt, а не за годинником: якщо браузер
  // підвис на старті (типово при відкритті Mini App), затяжки не "пропускаються"
  let t = 0;
  let raf = 0;
  let progress = 0;
  let shownProgress = -1;
  let finishing = false;
  let doneFired = false;

  const frame = (now) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    t += dt * 1000;
    const time = t / 1000;

    if (!doneFired && t >= expectedMs) {
      doneFired = true;
      onDone?.();
    }

    // Прогрес
    // До expectedMs доходимо приблизно до 90%, решту добігаємо на finish()
    const target = finishing ? 100 : 96 * (1 - Math.exp((-3 * t) / expectedMs));
    progress += (target - progress) * Math.min(1, dt * (finishing ? 10 : 5));
    const rounded = Math.min(100, Math.round(progress));
    if (rounded !== shownProgress) {
      shownProgress = rounded;
      onProgress(rounded);
    }

    if (reducedMotion) {
      raf = requestAnimationFrame(frame);
      return;
    }

    // Затяжки за розкладом
    for (const e of state) {
      const local = t - e.offset;
      const firing = local >= 0 && local % CYCLE_MS < e.duration;
      if (firing !== e.firing) {
        e.firing = firing;
        root.classList.toggle(`is-firing-${e.id}`, firing);
      }
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Боке
    for (let i = 0; i < bokeh.length; i++) {
      const b = bokeh[i];
      b.y += b.vy * dt;
      b.x += Math.sin(time * 0.5 + b.phase) * 6 * dt;
      if (b.y < -30) bokeh[i] = spawnBokeh(false);
      const size = b.r * 2;
      ctx.globalAlpha = b.alpha * (0.6 + 0.4 * Math.sin(time * 1.7 + b.phase));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.drawImage(b.sprite, b.x - b.r, b.y - b.r, size, size);
    }
    ctx.globalAlpha = 1;

    raf = requestAnimationFrame(frame);
  };

  raf = requestAnimationFrame(frame);

  return {
    // Завершення: прогрес добігає до 100%
    finish() {
      finishing = true;
    },
    stop() {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      state.forEach((e) => root.classList.remove(`is-firing-${e.id}`));
    },
  };
}
